/**
 * ConfirmModal - Standard two-step confirmation modal.
 *
 * Implements the confirmation pattern from STREAM_UX_PATTERNS.md Section 6.
 * Uses severity levels to style the modal appropriately for the action.
 *
 * @example Basic usage
 * ```tsx
 * <ConfirmModal
 *   open={showConfirm}
 *   onOpenChange={setShowConfirm}
 *   title="Archive customer?"
 *   onConfirm={handleArchive}
 *   onCancel={() => setShowConfirm(false)}
 * />
 * ```
 *
 * @example With severity and custom description
 * ```tsx
 * <ConfirmModal
 *   open={showConfirm}
 *   onOpenChange={setShowConfirm}
 *   title="Revoke access?"
 *   severity="danger"
 *   description="This will immediately revoke all API access for this user."
 *   confirmLabel="Revoke Access"
 *   onConfirm={handleRevoke}
 *   onCancel={() => setShowConfirm(false)}
 * />
 * ```
 */

import { useCallback, type CSSProperties, type ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Info, AlertTriangle, AlertCircle, X, Loader2 } from 'lucide-react'
import { Button } from './ui/button'

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * Severity levels for the confirm modal.
 * - `info`: Informational confirmation (neutral styling)
 * - `warning`: Cautionary confirmation (amber accent)
 * - `danger`: Destructive action confirmation (red accent)
 */
export type ConfirmModalSeverity = 'info' | 'warning' | 'danger'

/**
 * Size variants for the modal.
 */
export type ConfirmModalSize = 'sm' | 'md'

export interface ConfirmModalProps {
  /** Whether the modal is open */
  open: boolean
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void
  /** Modal title */
  title: string
  /** Modal severity level (default: 'warning') */
  severity?: ConfirmModalSeverity
  /** Size variant (default: 'sm') */
  size?: ConfirmModalSize
  /** Confirm button label (default: 'Confirm') */
  confirmLabel?: string
  /** Cancel button label (default: 'Cancel') */
  cancelLabel?: string
  /** Called when confirm is clicked */
  onConfirm: () => void | Promise<void>
  /** Called when cancel is clicked */
  onCancel?: () => void
  /** Whether the confirm action is in progress */
  isPending?: boolean
  /** Error message to display inline */
  error?: string | null

  // ── Slots ──────────────────────────────────────────────────────────────────

  /** Custom icon to display (default: severity-mapped icon) */
  icon?: ReactNode
  /** Description content below the title */
  description?: ReactNode
  /** Additional content between cancel and confirm buttons */
  trailing?: ReactNode
}

// ── Severity Configuration ───────────────────────────────────────────────────

const severityConfig: Record<
  ConfirmModalSeverity,
  { icon: typeof Info; iconColor: string; buttonVariant: 'primary' | 'danger' | 'secondary' }
> = {
  info: {
    icon: Info,
    iconColor: 'var(--info)',
    buttonVariant: 'primary',
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'var(--confirm-modal-warning-accent)',
    buttonVariant: 'primary', // warning uses primary with warning color
  },
  danger: {
    icon: AlertCircle,
    iconColor: 'var(--confirm-modal-danger-accent)',
    buttonVariant: 'danger',
  },
}

// ── Size Configuration ───────────────────────────────────────────────────────

const sizeConfig: Record<ConfirmModalSize, { maxWidth: string }> = {
  sm: { maxWidth: '400px' },
  md: { maxWidth: '480px' },
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * ConfirmModal provides a standard two-step confirmation dialog.
 *
 * Severity determines the visual styling:
 * - `info`: Blue icon, primary button
 * - `warning`: Amber icon, warning-styled button
 * - `danger`: Red icon, danger button
 *
 * Focus behavior:
 * - For destructive actions (danger), focus starts on Cancel
 * - Esc key cancels (except when isPending=true)
 */
export function ConfirmModal({
  open,
  onOpenChange,
  title,
  severity = 'warning',
  size = 'sm',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  isPending = false,
  error,
  icon,
  description,
  trailing,
}: ConfirmModalProps): ReactNode {
  const config = severityConfig[severity]
  const sizeStyles = sizeConfig[size]
  const IconComponent = config.icon

  // Handle confirm with async support
  const handleConfirm = useCallback(async () => {
    await onConfirm()
  }, [onConfirm])

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (!isPending) {
      onCancel?.()
      onOpenChange(false)
    }
  }, [isPending, onCancel, onOpenChange])

  // Handle escape key
  const handleEscapeKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isPending) {
        event.preventDefault()
      }
    },
    [isPending]
  )

  // Button style for warning severity (custom button color)
  const confirmButtonStyle: CSSProperties | undefined =
    severity === 'warning'
      ? { background: 'var(--confirm-modal-warning-accent)', color: 'white' }
      : undefined

  return (
    <DialogPrimitive.Root open={open} onOpenChange={isPending ? undefined : onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay style={styles.overlay} className="dialog-overlay" />
        <DialogPrimitive.Content
          style={{ ...styles.content, maxWidth: sizeStyles.maxWidth }}
          className="dialog-content"
          role="alertdialog"
          aria-describedby={description ? 'confirm-modal-description' : undefined}
          onEscapeKeyDown={handleEscapeKeyDown}
        >
          {/* Header with icon and title */}
          <div style={styles.header}>
            <div style={{ ...styles.iconWrapper, color: config.iconColor }}>
              {icon ?? <IconComponent size={20} />}
            </div>
            <DialogPrimitive.Title style={styles.title}>{title}</DialogPrimitive.Title>
          </div>

          {/* Description */}
          {description && (
            <DialogPrimitive.Description id="confirm-modal-description" style={styles.description}>
              {description}
            </DialogPrimitive.Description>
          )}

          {/* Error message */}
          {error && (
            <div style={styles.error}>
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div style={styles.actions}>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCancel}
              disabled={isPending}
              autoFocus={severity === 'danger'}
            >
              {cancelLabel}
            </Button>

            {trailing}

            <Button
              variant={config.buttonVariant}
              size="sm"
              onClick={handleConfirm}
              disabled={isPending}
              style={confirmButtonStyle}
              autoFocus={severity !== 'danger'}
            >
              {isPending && <Loader2 size={14} className="animate-spin" />}
              {confirmLabel}
            </Button>
          </div>

          {/* Close button */}
          <DialogPrimitive.Close
            style={styles.closeButton}
            className="dialog-close"
            disabled={isPending}
            aria-label="Close"
          >
            <X size={16} />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 50,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
  },
  content: {
    position: 'fixed',
    left: '50%',
    top: '50%',
    zIndex: 50,
    width: '100%',
    transform: 'translate(-50%, -50%)',
    borderRadius: 'var(--confirm-modal-radius)',
    border: '1px solid var(--confirm-modal-border)',
    background: 'var(--confirm-modal-bg)',
    padding: 'var(--confirm-modal-padding)',
    boxShadow: 'var(--shadow-lg)',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '12px',
  },
  iconWrapper: {
    flexShrink: 0,
    marginTop: '2px',
  },
  title: {
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    lineHeight: 1.4,
    margin: 0,
    paddingRight: '24px',
  },
  description: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
    margin: 0,
    marginBottom: '16px',
    marginLeft: '32px', // Align with title (icon width + gap)
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: 'var(--error)',
    marginBottom: '16px',
    marginLeft: '32px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '8px',
  },
  closeButton: {
    position: 'absolute',
    right: '12px',
    top: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  },
}

export default ConfirmModal
